import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { quotesAPI, clientsAPI, tasksAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatCurrency, formatDate, getStatusColor, QUOTE_STATUSES } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Printer, FileText, Send, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full logo URL
const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("/api")) {
    return `${BACKEND_URL}${logoUrl}`;
  }
  return logoUrl;
};

export default function QuoteDetailPage() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const printRef = useRef();
  
  const isNew = quoteId === "new";
  const clientIdParam = searchParams.get("client_id");

  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_id: clientIdParam || "",
    title: "",
    notes: "",
    valid_until: "",
    lines: [],
  });

  // New line dialog
  const [showLineDialog, setShowLineDialog] = useState(false);
  const [lineForm, setLineForm] = useState({
    description: "",
    quantity: "1",
    unit_price: "",
    task_id: "",
  });

  useEffect(() => {
    if (isNew) {
      fetchClients();
      if (clientIdParam) {
        fetchClientData(clientIdParam);
      }
    } else {
      fetchQuote();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, clientIdParam]);

  const fetchClients = async () => {
    try {
      const res = await clientsAPI.list({ status: "active" });
      setClients(res.data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchClientData = async (clientId) => {
    try {
      const [clientRes, tasksRes] = await Promise.all([
        clientsAPI.get(clientId),
        tasksAPI.list({ client_id: clientId }),
      ]);
      setClient(clientRes.data);
      setTasks(tasksRes.data);
    } catch (err) {
      console.error("Error fetching client data:", err);
    }
  };

  const fetchQuote = async () => {
    try {
      const res = await quotesAPI.get(quoteId);
      setQuote(res.data);
      setFormData({
        client_id: res.data.client_id,
        title: res.data.title,
        notes: res.data.notes || "",
        valid_until: res.data.valid_until || "",
        lines: res.data.lines || [],
      });
      
      // Fetch client data
      const [clientRes, tasksRes] = await Promise.all([
        clientsAPI.get(res.data.client_id),
        tasksAPI.list({ client_id: res.data.client_id }),
      ]);
      setClient(clientRes.data);
      setTasks(tasksRes.data);
    } catch (err) {
      toast.error("Quote not found");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    setFormData({ ...formData, client_id: clientId });
    fetchClientData(clientId);
  };

  const handleAddLine = () => {
    const qty = parseFloat(lineForm.quantity) || 0;
    const price = parseFloat(lineForm.unit_price) || 0;
    if (!lineForm.description || price <= 0) {
      toast.error("Please fill in description and price");
      return;
    }
    const newLine = {
      ...lineForm,
      id: `temp-${Date.now()}`,
      quantity: qty,
      unit_price: price,
      amount: qty * price,
      task_id: lineForm.task_id || null,
    };
    setFormData({
      ...formData,
      lines: [...formData.lines, newLine],
    });
    setLineForm({ description: "", quantity: "1", unit_price: "", task_id: "" });
    setShowLineDialog(false);
  };

  const handleRemoveLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const handleAddFromTask = (task) => {
    const newLine = {
      id: `temp-${Date.now()}`,
      description: task.title,
      quantity: task.estimated_hours || 1,
      unit_price: task.rate || client?.default_rate || 0,
      amount: (task.estimated_hours || 1) * (task.rate || client?.default_rate || 0),
      task_id: task.id,
    };
    setFormData({
      ...formData,
      lines: [...formData.lines, newLine],
    });
    toast.success("Task added to quote");
  };

  const calculateTotal = () => {
    return formData.lines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
  };

  const handleSave = async () => {
    if (!formData.client_id || !formData.title || formData.lines.length === 0) {
      toast.error("Please fill in all required fields and add at least one line");
      return;
    }

    setSaving(true);
    try {
      const data = {
        client_id: formData.client_id,
        title: formData.title,
        notes: formData.notes || null,
        valid_until: formData.valid_until || null,
        lines: formData.lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          task_id: l.task_id || null,
        })),
      };

      if (isNew) {
        const res = await quotesAPI.create(data);
        toast.success("Quote created");
        navigate(`/quotes/${res.data.id}`);
      } else {
        await quotesAPI.update(quoteId, data);
        toast.success("Quote updated");
        fetchQuote();
      }
    } catch (err) {
      toast.error("Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await quotesAPI.update(quoteId, { status: newStatus });
      toast.success(`Quote marked as ${newStatus}`);
      fetchQuote();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const total = calculateTotal();
  const currency = client?.currency || "PHP";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quote-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isNew ? "New Quote" : `Quote ${quote?.quote_number}`}
            </h1>
            {!isNew && quote && (
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusColor(quote.status)}>{quote.status}</Badge>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">{client?.name}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && quote?.status === "draft" && (
            <>
              <Button variant="outline" onClick={() => handleStatusChange("sent")}>
                <Send className="h-4 w-4 mr-2" />
                Mark Sent
              </Button>
            </>
          )}
          {!isNew && quote?.status === "sent" && (
            <>
              <Button variant="outline" onClick={() => handleStatusChange("approved")} className="text-green-600">
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button variant="outline" onClick={() => handleStatusChange("rejected")} className="text-red-600">
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Print-ready Quote */}
      <div ref={printRef} className="invoice-container print:shadow-none">
        {/* Header */}
        <div className="invoice-header">
          <div>
            {user?.logo_url && (
              <img src={getLogoUrl(user.logo_url)} alt="Logo" className="invoice-logo mb-4" />
            )}
            <h2 className="text-2xl font-bold">{user?.business_name || user?.name}</h2>
            {user?.address && <p className="text-sm text-muted-foreground whitespace-pre-line">{user.address}</p>}
            {user?.phone && <p className="text-sm text-muted-foreground">{user.phone}</p>}
            {user?.email && <p className="text-sm text-muted-foreground">{user.email}</p>}
            {user?.tax_id && <p className="text-sm text-muted-foreground">Tax ID: {user.tax_id}</p>}
          </div>
          <div className="invoice-meta">
            <h1 className="text-3xl font-bold text-muted-foreground mb-2">QUOTE</h1>
            {!isNew && <p className="font-mono text-lg">{quote?.quote_number}</p>}
            <p className="text-sm text-muted-foreground mt-2">
              Date: {formatDate(quote?.created_at || new Date().toISOString())}
            </p>
            {formData.valid_until && (
              <p className="text-sm text-muted-foreground">
                Valid Until: {formatDate(formData.valid_until)}
              </p>
            )}
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Quote For</p>
          {isNew && !client ? (
            <div className="no-print">
              <Select value={formData.client_id} onValueChange={handleClientChange}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <p className="font-semibold text-lg">{client?.name}</p>
              {client?.address && <p className="text-sm text-muted-foreground">{client.address}</p>}
              {client?.email && <p className="text-sm text-muted-foreground">{client.email}</p>}
            </div>
          )}
        </div>

        {/* Quote Title */}
        <div className="mb-6 no-print">
          <Label>Quote Title *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Website Development Project"
            className="mt-1"
          />
        </div>
        {!isNew && (
          <div className="mb-6 print-only">
            <p className="font-semibold">{formData.title}</p>
          </div>
        )}

        {/* Line Items */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4 no-print">
            <h3 className="font-semibold">Line Items</h3>
            <div className="flex gap-2">
              {tasks.length > 0 && (
                <Select onValueChange={(taskId) => {
                  const task = tasks.find(t => t.id === taskId);
                  if (task) handleAddFromTask(task);
                }}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Add from task" />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks.filter(t => !formData.lines.some(l => l.task_id === t.id)).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" onClick={() => setShowLineDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Line
              </Button>
            </div>
          </div>

          <table className="w-full invoice-table">
            <thead>
              <tr>
                <th className="rounded-l-lg">Description</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right rounded-r-lg">Amount</th>
                <th className="w-12 no-print"></th>
              </tr>
            </thead>
            <tbody>
              {formData.lines.map((line, index) => (
                <tr key={line.id || index}>
                  <td>{line.description}</td>
                  <td className="text-right font-mono">{line.quantity}</td>
                  <td className="text-right font-mono">{formatCurrency(line.unit_price, currency)}</td>
                  <td className="text-right font-mono">{formatCurrency(line.quantity * line.unit_price, currency)}</td>
                  <td className="no-print">
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveLine(index)} className="h-8 w-8 text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {formData.lines.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted-foreground py-8">
                    No line items yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="invoice-totals">
          <div className="invoice-grand-total">
            <span>Total</span>
            <span className="font-mono">{formatCurrency(total, currency)}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="mt-8">
          <Label className="no-print">Notes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes or terms..."
            rows={3}
            className="mt-1 no-print"
          />
          {formData.notes && (
            <div className="print-only mt-4 p-4 bg-muted rounded-lg print:bg-gray-100 print:text-black">
              <p className="text-sm whitespace-pre-line print:text-black">{formData.notes}</p>
            </div>
          )}
        </div>

        {/* Valid Until */}
        <div className="mt-4 no-print">
          <Label>Valid Until</Label>
          <Input
            type="date"
            value={formData.valid_until}
            onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
            className="mt-1 w-48"
          />
        </div>
      </div>

      {/* Add Line Dialog */}
      <Dialog open={showLineDialog} onOpenChange={setShowLineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={lineForm.description}
                onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                placeholder="Service or item description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={lineForm.quantity}
                  onChange={(e) => setLineForm({ ...lineForm, quantity: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Price *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={lineForm.unit_price}
                  onChange={(e) => setLineForm({ ...lineForm, unit_price: e.target.value })}
                />
              </div>
            </div>
            <div className="text-right text-lg font-semibold">
              Amount: {formatCurrency((parseFloat(lineForm.quantity) || 0) * (parseFloat(lineForm.unit_price) || 0), currency)}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLineDialog(false)}>Cancel</Button>
            <Button onClick={handleAddLine}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
